/* TypeOfDietPage.jsx (Step 4) */
import React, { useEffect, useMemo, useState } from "react";
import { trackFunnel } from "../../../services/funnelTracker";
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonLoading,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { updateMasterClubSubscriber } from "../../../redux/actions/initialConfigActions";
import { baseUrl, headersDefault } from "../../../redux/actions";
import { setInitialConfigProperty } from "../../../redux/reducer/initialConfigSlice";
import { setAdoc_agreements } from "../../../redux/reducer/userSlice";

import { writeAuth } from "../../../services/authStorage";
const FALLBACK_DIETS = [
  { id: 1, name: "Onnivoro", is_enabled: true },
  { id: 3, name: "Vegetariano", is_enabled: true },
  { id: 2, name: "Ovo-pesco vegetale", is_enabled: true },
];

function escapeHtml(s = "") {
  return String(s).replace(/[&<>"']/g, (c) => {
	switch (c) {
	  case "&":
		return "&amp;";
	  case "<":
		return "&lt;";
	  case ">":
		return "&gt;";
	  case '"':
		return "&quot;";
	  case "'":
		return "&#039;";
	  default:
		return c;
	}
  });
}


function dietKind(name = "") {
  const n = String(name).toLowerCase();
  if (n.includes("ovo") || n.includes("pesco")) return "ovo";
  if (n.includes("onnivor")) return "onnivoro";
  if (n.includes("vegetarian")) return "vegetariano";
  return "other";
}

function DietName({ name }) {
  const kind = dietKind(name);
  // match mock: split in 2 lines and centered row
  if (kind === "ovo") {
	return <>OVO-PESCO VEGETALE</>;
  }
  return String(name || "").toUpperCase();
}

function dietSubtitle(name = "") {
  const n = name.toLowerCase();
  if (n.includes("onnivor")) return "Se mangi tutto";
  if (n.includes("vegetarian") && !n.includes("ovo"))
	return "Se non mangi carne e pesce";
  if (n.includes("ovo") || n.includes("pesco"))
	return "Se mangi tutto tranne la carne";
  return "";
}

function isVegetarianDiet(name = "") {
  const n = name.toLowerCase();
  // strict vegetarian only
  return n.includes("vegetarian") && !n.includes("ovo") && !n.includes("pesco");
}

export default function TypeOfDietPage() {
  const history = useHistory();
  const dispatch = useDispatch();

  const is_smart = useSelector((s) => s.initialConfig?.is_smart);
  const initialVeg = useSelector((s) => s.initialConfig?.vegetarian);
  const initialGlutenFree = useSelector((s) => s.initialConfig?.gluten_free);
  const initialKeepWeight = useSelector((s) => s.initialConfig?.keep_weight);
  const [keepWeight, setKeepWeight] = useState(!!initialKeepWeight);

  const [vegetarian, setVegetarian] = useState(
	typeof initialVeg === "boolean" ? initialVeg : null
  );

  const [glutenFree, setGlutenFree] = useState(
	typeof initialGlutenFree === "boolean" ? initialGlutenFree : false
  );

  const [diets, setDiets] = useState([]);
  const [loadingDiets, setLoadingDiets] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ✅ Agreements (Phase 3C)
  // Load agreements list from backend and keep local acceptance timestamps.
  const [agreementsList, setAgreementsList] = useState([]);
  const [agreementsById, setAgreementsById] = useState({});
  const [loadingAgreements, setLoadingAgreements] = useState(true);
  const [agreementsLoadError, setAgreementsLoadError] = useState("");

  const [docModal, setDocModal] = useState({
	isOpen: false,
	title: "",
	content: "",
  });
  const [docLoading, setDocLoading] = useState(false);

  useEffect(() => {
	let alive = true;

	async function loadAgreements() {
	  setLoadingAgreements(true);
	  setAgreementsLoadError("");

	  try {
		const res = await fetch(`${baseUrl}/api/v1/users/agreements/list`, {
		  method: "GET",
		  headers: headersDefault(),
		});

		const data = await res.json().catch(() => null);
		if (!res.ok) {
		  throw new Error(data?.message ?? "Errore caricamento consensi");
		}

		const list = Array.isArray(data?.data)
		  ? data.data
		  : Array.isArray(data)
		  ? data
		  : [];

		const enabled = list.filter((a) => a?.is_enabled !== false);

		if (!alive) return;

		setAgreementsList(enabled);

		const initial = {};
		enabled.forEach((a) => {
		  initial[a.id] = {
			checked: !!a.is_accepted,
			acceptedAt: a.accepted_at ?? null,
		  };
		});
		setAgreementsById(initial);
	  } catch (e) {
		if (!alive) return;
		setAgreementsList([]);
		setAgreementsById({});
		setAgreementsLoadError(e?.message ?? "Errore rete");
	  } finally {
		if (alive) setLoadingAgreements(false);
	  }
	}

	loadAgreements();
	return () => {
	  alive = false;
	};
  }, []);

  // Only the optional consents are asked here. The Terms are accepted on the
  // sign-in screen ("Continuando accetti…"), so there's no required checkbox.
  const optionalAgreements = useMemo(
	() => agreementsList.filter((a) => !a?.is_required),
	[agreementsList]
  );

  const closeDocModal = () => {
	setDocLoading(false);
	setDocModal({ isOpen: false, title: "", content: "" });
  };

	const resolveDocumentHtml = async (doc) => {
	 const title = doc?.title || "Documento";
	 if (!doc) return { title, html: "<p>Documento non disponibile.</p>" };
 
	 if (doc.content) {
	   return { title, html: doc.content };
	 }
 
	 if (!doc.url) {
	   return { title, html: "<p>Documento non disponibile.</p>" };
	 }
 
	 const h = headersDefault();
	 delete h["Content-Type"];
	 h.Accept = "text/html, application/json";
 
	 const res = await fetch(doc.url, { method: "GET", headers: h });
	 const raw = await res.text();
 
	 // endpoint may return raw HTML OR JSON with { content }
	 let html = raw;
	 try {
	   const j = JSON.parse(raw);
	   html = j?.content ?? j?.data?.content ?? j?.data?.html ?? j?.html ?? raw;
	 } catch (_) {}
 
	 return { title, html };
   };
 
   const openAgreementDocumentsModal = async (agreement) => {
	 const docs = Array.isArray(agreement?.documents) ? agreement.documents : [];
 
	 setDocModal({
	   isOpen: true,
	   title: agreement?.title || "Documenti",
	   content: "<p>Caricamento...</p>",
	 });
 
	 if (!docs.length) {
	   setDocModal({
		 isOpen: true,
		 title: agreement?.title || "Documenti",
		 content: "<p>Nessun documento disponibile.</p>",
	   });
	   return;
	 }
 
	 setDocLoading(true);
	 try {
	   const resolved = await Promise.all(
		 docs.map(async (d) => {
		   try {
			 return await resolveDocumentHtml(d);
		   } catch (_) {
			 return {
			   title: d?.title || "Documento",
			   html: "<p>Impossibile caricare il documento.</p>",
			 };
		   }
		 })
	   );
 
	   // Divider BEFORE each document   bold title   content
	   const html = resolved
		 .map((r, idx) => {
		   const safeTitle = escapeHtml(r.title || "Documento");
		   return `
			 <div>${r.html || "<p>Documento non disponibile.</p>"}</div>
		   `;
		 })
		 .join("");
 
	   setDocModal({
		 isOpen: true,
		 title: agreement?.title || "Documenti",
		 content: html || "<p>Nessun documento disponibile.</p>",
	   });
	 } finally {
	   setDocLoading(false);
	 }
   };

  const openAgreementDocumentModal = async (agreement, doc) => {
    if (!doc) return;

    setDocModal({
      isOpen: true,
      title: doc.title || agreement?.title || "Documento",
      content: "<p>Caricamento...</p>",
    });

    try {
      const r = await resolveDocumentHtml(doc);

      const safeTitle = escapeHtml(r.title || doc.title || "");
      const content = `<div>${
        r.html || ""
      }</div>`;

      setDocModal({
        isOpen: true,
        title: r.title || doc.title || agreement?.title || "Documento",
        content,
      });
    } catch (e) {
      console.error("Failed to open agreement document", e);
      setDocModal({
        isOpen: true,
        title: doc.title || agreement?.title || "Documento",
        content:
          "<p>Impossibile caricare il documento in questo momento. Riprova più tardi.</p>",
      });
    }
  };

  const getAgreementDocMatchRanges = (agreementTitle, documents = []) => {
    const title = agreementTitle || "";
    const titleLower = title.toLowerCase();

    const findRangeForDoc = (docTitle) => {
      if (!docTitle) return null;

      const docTitleNorm = String(docTitle).replace(/\s+/g, " ").trim();
      const docLower = docTitleNorm.toLowerCase();

      // 1) exact match
      let idx = titleLower.indexOf(docLower);
      if (idx !== -1) {
        return { start: idx, end: idx + docTitleNorm.length };
      }

      // 2) best contiguous phrase from doc title (prefer from beginning)
      const words = docTitleNorm.split(" ").filter(Boolean);
      if (!words.length) return null;

      const maxLen = Math.min(words.length, 6);
      for (let len = maxLen; len >= 2; len--) {
        const phrase = words.slice(0, len).join(" ");
        idx = titleLower.indexOf(phrase.toLowerCase());
        if (idx !== -1) return { start: idx, end: idx + phrase.length };
      }

      // 3) any contiguous phrase (fallback)
      for (let len = Math.min(words.length, 5); len >= 2; len--) {
        for (let start = 0; start <= words.length - len; start++) {
          const phrase = words.slice(start, start + len).join(" ");
          idx = titleLower.indexOf(phrase.toLowerCase());
          if (idx !== -1) return { start: idx, end: idx + phrase.length };
        }
      }

      return null;
    };

    const ranges = documents
      .map((doc) => {
        const range = findRangeForDoc(doc?.title);
        if (!range) return null;
        return {
          ...range,
          doc,
          text: title.slice(range.start, range.end),
        };
      })
      .filter(Boolean);

    // sort by start then by longer first
    ranges.sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return b.end - b.start - (a.end - a.start);
    });

    // remove overlaps (keep first/longest)
    const cleaned = [];
    let cursor = -1;
    for (const r of ranges) {
      if (r.start >= cursor) {
        cleaned.push(r);
        cursor = r.end;
      }
    }

    return cleaned;
  };

  const renderAgreementTitleWithDocLinks = (agreement) => {
    const title = agreement?.title || "";
    const docs = agreement?.documents || [];

    const ranges = getAgreementDocMatchRanges(title, docs);

    // If we can't map any part of the title, fall back to linking the whole label
    if (!ranges.length) {
      return (
        <span
          className="ds-init__agreementLink"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            openAgreementDocumentsModal(agreement);
          }}
          onKeyDown={onAgreementLinkKeyDown(() => openAgreementDocumentsModal(agreement))}
        >
          {title}
        </span>
      );
    }

    let pos = 0;
    const parts = [];

    ranges.forEach((r, idx) => {
      if (r.start > pos) {
        parts.push({
          key: `t_${idx}_${pos}`,
          text: title.slice(pos, r.start),
          doc: null,
        });
      }

      parts.push({
        key: `d_${idx}_${r.start}`,
        text: title.slice(r.start, r.end),
        doc: r.doc,
      });

      pos = r.end;
    });

    if (pos < title.length) {
      parts.push({ key: `t_end_${pos}`, text: title.slice(pos), doc: null });
    }

    return (
      <>
        {parts.map((p) =>
          p.doc ? (
            <span
              key={p.key}
              className="ds-init__agreementLink"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openAgreementDocumentModal(agreement, p.doc);
              }}
              onKeyDown={onAgreementLinkKeyDown(() =>
                openAgreementDocumentModal(agreement, p.doc)
              )}
            >
              {p.text}
            </span>
          ) : (
            <span key={p.key}>{p.text}</span>
          )
        )}
      </>
    );
  };




  const toggleAgreementId = (id) => {
	if (!id) return;

	setAgreementsById((prev) => {
	  const curr = prev?.[id] || { checked: false, acceptedAt: null };
	  const nextChecked = !curr.checked;

	  return {
		...prev,
		[id]: {
		  checked: nextChecked,
		  acceptedAt: nextChecked ? new Date().toISOString() : null,
		},
	  };
	});
  };

  const onAgreementKeyDown = (handler) => (e) => {
	if (e.key === "Enter" || e.key === " ") {
	  e.preventDefault();
	  handler();
	}
  };

  const onAgreementLinkKeyDown = (handler) => (e) => {
	if (e.key === "Enter" || e.key === " ") {
	  e.preventDefault();
	  e.stopPropagation();
	  handler();
	}
  };


  const dietLabel = useMemo(() => {
	if (vegetarian === true) return "Vegetariano";
	if (vegetarian === false) return "Onnivoro";
	return "";
  }, [vegetarian]);

  const toggleGlutenFree = () => {
	setGlutenFree((prev) => !prev);
  };

  const pickDiet = (isVeg) => {
	setVegetarian(!!isVeg);
  };

  //   useEffect(() => {
  //     let alive = true;

  //     async function load() {
  //       setLoadingDiets(true);
  //       setError("");
  //       try {
  //         const res = await fetch(`${baseUrl}/api/diet/list`, {
  //           method: "GET",
  //           headers: headersDefault(),
  //         });

  //         const data = await res.json().catch(() => null);
  //         if (!res.ok)
  //           throw new Error(data?.message ?? "Errore caricamento diete");

  //         const enabled = Array.isArray(data)
  //           ? data.filter((x) => x?.is_enabled !== false)
  //           : [];

  //         const list = enabled.length ? enabled : FALLBACK_DIETS;

  //         if (!alive) return;
  //         setDiets(list);

  //         // ✅ default selection if missing
  //         if (!diet_id && list.length) {
  //           const defaultDiet =
  //             list.find((d) => d.name?.toLowerCase().includes("onnivor")) ||
  //             list[0];

  //           dispatch(
  //             setInitialConfigProperty({ key: "diet_id", value: defaultDiet.id })
  //           );
  //           dispatch(
  //             setInitialConfigProperty({
  //               key: "vegetarian",
  //               value: isVegetarianDiet(defaultDiet.name),
  //             })
  //           );
  //         }
  //       } catch (e) {
  //         if (!alive) return;
  //         setDiets(FALLBACK_DIETS);

  //         // still set a default if none
  //         if (!diet_id) {
  //           const defaultDiet = FALLBACK_DIETS.find((d) =>
  //             d.name?.toLowerCase().includes("onnivor")
  //           );
  //           if (defaultDiet) {
  //             dispatch(
  //               setInitialConfigProperty({
  //                 key: "diet_id",
  //                 value: defaultDiet.id,
  //               })
  //             );
  //             dispatch(
  //               setInitialConfigProperty({ key: "vegetarian", value: false })
  //             );
  //           }
  //         }

  //         setError(e?.message ?? "Errore rete");
  //       } finally {
  //         if (alive) setLoadingDiets(false);
  //       }
  //     }

  //     load();
  //     return () => {
  //       alive = false;
  //     };
  //   }, [dispatch]);

  const user = useSelector((s) => s.user?.user);
  const subscriber = useSelector((s) => s.user?.master_club_subscriber);

  const subscriptionName = (
	subscriber?.subscription?.subscription_type?.name ??
	user?.subscription_label?.name ??
	"guest"
  )
	.toLowerCase()
	.trim();

  console.log("subscriptionName", subscriptionName);

  const isGuest = subscriptionName === "guest";

  console.log("isGuest", isGuest);

  const selectDiet = (diet) => {
	dispatch(setInitialConfigProperty({ key: "diet_id", value: diet.id }));
	dispatch(
	  setInitialConfigProperty({
		key: "vegetarian",
		value: isVegetarianDiet(diet.name),
	  })
	);
  };

  const setMenuSmart = (val) => {
	dispatch(setInitialConfigProperty({ key: "is_smart", value: !!val }));
  };

  const handleBack = () => {
	history.replace("/impostazioni-iniziali/2");
  };

	const getCheckedAgreementIds = () => {
	// only include enabled + checked
	return optionalAgreements
	  .filter((a) => a?.is_enabled !== false)
	  .map((a) => a?.id)
	  .filter((id) => !!id && !!agreementsById?.[id]?.checked);
  };

  const submitAcceptedAgreements = async (agreementIds) => {
	// Only the optional consents the member ticked; nothing to send if none.
	if (!Array.isArray(agreementIds) || agreementIds.length === 0) return;

	const h = headersDefault();
	if (!h["Content-Type"]) h["Content-Type"] = "application/json";
	h.Accept = "application/json";

	const res = await fetch(`${baseUrl}/api/v1/users/agreements/accept`, {
	  method: "POST",
	  headers: h,
	  body: JSON.stringify({ agreements: agreementIds }),
	});

	const data = await res.json().catch(() => null);
	if (!res.ok) {
	  throw new Error(data?.message ?? "Errore salvataggio consensi");
	}

	return data;
  };


  const handleFinish = async () => {
	setError("");

	if (typeof vegetarian !== "boolean") {
	  setError("Seleziona un tipo di alimentazione.");
	  setSaving(false);
	  return;
	}

	setSaving(true);

	const subExtras = Array.isArray(subscriber?.extras)
	  ? {}
	  : subscriber?.extras || {};

	const payload = {
	  is_smart: !!is_smart,
	  keep_weight: keepWeight,
	  vegetarian,
	  gluten_free: glutenFree,
	  extras: {
		...subExtras,
		type_of_diet_done: true,
	  },
	};

	  try {
	  const checkedIds = getCheckedAgreementIds();

	  const acceptResp = await submitAcceptedAgreements(checkedIds);

	  // Keep agreements cache in sync so onboarding gate can't be bypassed (Phase 5)
	  try {
		// Prefer server response if it includes an updated agreements list
		const serverList =
		  acceptResp?.data?.data ||
		  acceptResp?.data ||
		  acceptResp?.agreements ||
		  acceptResp?.data?.agreements ||
		  null;

		const updatedAgreements = Array.isArray(serverList)
		  ? serverList
		  : (agreementsList ?? []).map((a) => {
			  const isAccepted = !!agreementsById?.[a?.id]?.checked;
			  return isAccepted
				? { ...a, is_accepted: true, accepted_at: a?.accepted_at ?? new Date().toISOString() }
				: { ...a, is_accepted: false };
			});

		writeAuth("adoc_agreements", JSON.stringify(updatedAgreements));
		dispatch(setAdoc_agreements(updatedAgreements));
	  } catch (_) {
		// ignore cache errors
	  }


	  // ✅ Existing: update subscriber onboarding extras/preferences
	  await dispatch(
		updateMasterClubSubscriber({
		  id: 1,
		  method: "PATCH",
		  payload,
		})
	  ).unwrap();

	  dispatch(
		setInitialConfigProperty({ key: "keep_weight", value: keepWeight })
	  );
	  dispatch(
		setInitialConfigProperty({ key: "vegetarian", value: vegetarian })
	  );
	  dispatch(
		setInitialConfigProperty({ key: "gluten_free", value: glutenFree })
	  );
	  dispatch(setInitialConfigProperty({ key: "diet_id", value: null }));

	  // { fromOnboarding: true } — signals to WeHaveFinished that the
	  // user just finished step 5 of the wizard, so the path-picker
	  // should be shown unconditionally (no bypass-to-/home) even if
	  // the user already happens to carry an active paid subscription
	  // at this moment. See WeHaveFinished.js bypass guard.
	  trackFunnel("onboarding_step_completed", { step: 5 });
	  trackFunnel("onboarding_completed");
	  // RouteAuth usually got there first: the refreshed profile shows onboarding
	  // complete and it redirects to /onboarding-complete itself. Navigating again
	  // raced that routing and could leave the member on its spinner.
	  if (history.location.pathname.startsWith("/impostazioni-iniziali")) {
		history.replace("/onboarding-complete", { fromOnboarding: true });
	  }
	} catch (e) {
	  setError(typeof e === "string" ? e : e?.message ?? "Errore salvataggio");
	} finally {
	  setSaving(false);
	}

  };

  return (
	<IonPage className="ds-init ds-init--diet">
	  <IonContent fullscreen className="ds-init__content">
		<div className="ds-init__wrap">
		  {/* progress (3/3 active) */}
		  <div className="ds-init__progress">
		  	<span className="is-done" />
		  	<span className="is-done" />
		  	<span className="is-active" />
		  </div>

		  <div className="ds-init__section">
			<div className="ds-init__title">
			  Ultimo passo! Indica le tue preferenze per creare il menù perfetto
			  per te.
			</div>

			{/* Dimagrimento / Mantenimento */}
			<div className="ds-init__prefTop">
			  <button
				className={`ds-init__checkBtn ds-init__caps ${
				  !keepWeight ? "is-on" : ""
				}`}
				onClick={() => setKeepWeight(false)}
				type="button"
			  >
				<span className="ds-init__box" />
				<span className="ds-init__prefLabel">DIMAGRIMENTO</span>
			  </button>

			  <button
				className={`ds-init__checkBtn ds-init__caps ${
				  keepWeight ? "is-on" : ""
				}`}
				onClick={() => setKeepWeight(true)}
				type="button"
			  >
				<span className="ds-init__box" />
				<span className="ds-init__prefLabel">MANTENIMENTO</span>
			  </button>
			</div>

			<div className="ds-init__divider" />

			{/* Diet types (2 columns like mock) */}
			<div className="ds-init__dietGrid">
			  <button
				type="button"
				className={`ds-init__checkBtn ds-init__caps ${
				  vegetarian === false ? "is-on" : ""
				}`}
				onClick={() => pickDiet(false)}
			  >
				<span
				  className={`ds-init__dietBox ${
					vegetarian === false ? "is-on" : ""
				  }`}
				/>
				<span className="ds-init__dietTxt">
				  <span className="ds-init__dietName">
					<DietName name="ONNIVORO" />
				  </span>
				  <span className="ds-init__dietSub">Se mangi tutto</span>
				</span>
			  </button>

			  <button
				type="button"
				className={`ds-init__checkBtn ds-init__caps ${
				  vegetarian === true ? "is-on" : ""
				}`}
				onClick={() => pickDiet(true)}
			  >
				<span
				  className={`ds-init__dietBox ${
					vegetarian === true ? "is-on" : ""
				  }`}
				/>
				<span className="ds-init__dietTxt">
				  <span className="ds-init__dietName">
					<DietName name="VEGETARIANO" />
				  </span>
				  <span className="ds-init__dietSub">
					Se non mangi carne e pesce
				  </span>
				</span>
			  </button>
			</div>

			<div className="ds-init__divider" />

			{/* Exclusions */}
			<div className="ds-init__centerRow">
			  <button
				className={`ds-init__checkBtn ds-init__caps ${
				  glutenFree ? "is-on" : ""
				}`}
				onClick={toggleGlutenFree}
				type="button"
			  >
				<span className="ds-init__box" />
				<span className="ds-init__prefLabel">
				  SENZA GLUTINE E LATTOSIO
				</span>
			  </button>
			</div>

			<div className="ds-init__divider" />

			{/* Menu format */}
			<div className="ds-init__menuGridWrap">
			  <span className="ds-init__recommend">consigliato</span>

			  <div className="ds-init__menuGrid">
				<button
				  type="button"
				  className={`ds-init__menuCard ${!is_smart ? "is-on" : ""}`}
				  onClick={() => setMenuSmart(false)}
				>
				  <div className="ds-init__menuTop">
					<span
					  className={`ds-init__dietBox ${!is_smart ? "is-on" : ""}`}
					/>
					<span className="ds-init__menuName">CLASSIC</span>
				  </div>
				  <div className="ds-init__menuDesc">
					Menù completi con ricette per ogni pasto, pensati anche per
					tutta la famiglia
				  </div>
				</button>

				<button
				  type="button"
				  className={`ds-init__menuCard ${is_smart ? "is-on" : ""}`}
				  onClick={() => setMenuSmart(true)}
				>
				  <div className="ds-init__menuTop">
					<span
					  className={`ds-init__dietBox ${is_smart ? "is-on" : ""}`}
					/>
					<span className="ds-init__menuName">SMART</span>
				  </div>
				  <div className="ds-init__menuDesc">
					Menù essenziali con indicazione dei soli alimenti previsti
					nel pasto.
				  </div>
				</button>
			  </div>
			</div>

			{error ? <div className="ds-init__error">{error}</div> : null}
<div className="ds-init__divider" />

			{/* Agreements */}
			<div className="ds-init__agreements">
			  {loadingAgreements ? (
				<div className="ds-init__agreementText">
				  Caricamento consensi...
				</div>
			  ) : null}

			  {agreementsLoadError ? (
				<div className="ds-init__agreementText">
				  {agreementsLoadError}
				</div>
			  ) : null}

							 {optionalAgreements.map((a) => {
				 const checked = !!agreementsById?.[a.id]?.checked;
				 const hasDocs =
				   Array.isArray(a?.documents) && a.documents.length > 0;
 
				 return (
				   <div
					 key={a.id}
					 className="ds-init__agreementRow"
					 role="button"
					 tabIndex={0}
					 onClick={() => {
					   toggleAgreementId(a.id);
					 }}
					 onKeyDown={onAgreementKeyDown(() => {
					   toggleAgreementId(a.id);
					 })}
				   >
					 <IonCheckbox checked={checked} style={{ pointerEvents: "none" }} />
					 <div className="ds-init__agreementText">
					   {hasDocs ? renderAgreementTitleWithDocLinks(a) : (a.title || "")}
					 </div>
				   </div>
				 );
			   })}
			</div>


		  </div>

		</div>

		<IonLoading isOpen={saving} message={"Salvataggio..."} />
		<IonLoading isOpen={docLoading} message={"Caricamento documento..."} />

		<IonModal isOpen={docModal.isOpen} onDidDismiss={closeDocModal}>
		  <IonHeader>
			<IonToolbar>
			  <IonTitle>{docModal.title}</IonTitle>
			  <IonButtons slot="end">
				<IonButton onClick={closeDocModal}>Chiudi</IonButton>
			  </IonButtons>
			</IonToolbar>
		  </IonHeader>

		  <IonContent>
			<div
			  className="ds-init__docModalBody"
			  dangerouslySetInnerHTML={{ __html: docModal.content }}
			/>
		  </IonContent>
		</IonModal>

	  </IonContent>

	  <IonFooter className="ds-init__footer--fixed">
		<div className="ds-init__footer ds-init__footer--two">
		  <IonButton
			fill="clear"
			className="ds-init__back"
			onClick={handleBack}
		  >
			INDIETRO
		  </IonButton>

		  <IonButton
			className="ds-init__next"
			onClick={handleFinish}
			disabled={saving}
		  >
			CI SIAMO!
		  </IonButton>
		</div>
	  </IonFooter>
	</IonPage>
  );
}